// Zombie-villager cure → villager gossip (MB.6 / MB.7). PURE (ENG-1).
//
// Curing a zombie villager (Weakness + golden apple; the timer lives in
// game/monsters.ts) doesn't just convert the mob back to a villager — in Java the
// freshly-cured villager gains strong `major_positive` gossip toward its curer,
// and other villagers who witness the cure gain a smaller share. That reputation
// flows straight into the trade-discount economy (game/gossip.ts →
// game/economy.ts), so a village trades cheaply with the hero who saved one of
// its own. This module is the pure grant computation; the host converts the
// entity and applies the grants to its per-villager gossip ledgers. It closes the
// loop the Pass-13 gossip slice could only simulate through a debug hook.
import type { GossipType } from './gossip';

/** The cured villager's gratitude — major_positive to the cap → a 0.30 discount. */
export const CURE_GOSSIP_MAJOR = 20;
/** A witnessing villager's smaller share of the good will. */
export const CURE_GOSSIP_SPREAD = 5;
/** Blocks within which other villagers witness the cure (Java gossip range). */
export const CURE_GOSSIP_RADIUS = 10;

export interface CureGossipGrant {
  id: number;
  type: GossipType;
  amount: number;
}

/**
 * The gossip a completed cure spreads. `curedId` is the entity id the zombie
 * villager became; `nearby` are the other villagers with their horizontal
 * distance to the cure. The cured villager always receives the strong grant;
 * villagers within `radius` receive the smaller witness share; villagers beyond
 * it receive nothing.
 */
export function cureGossipGrants(
  curedId: number,
  nearby: ReadonlyArray<{ id: number; dist: number }>,
  radius = CURE_GOSSIP_RADIUS,
): CureGossipGrant[] {
  const grants: CureGossipGrant[] = [{ id: curedId, type: 'major_positive', amount: CURE_GOSSIP_MAJOR }];
  for (const v of nearby) {
    if (v.id === curedId || v.dist > radius) continue;
    grants.push({ id: v.id, type: 'major_positive', amount: CURE_GOSSIP_SPREAD });
  }
  return grants;
}
